import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import FilledInput from '@material-ui/core/FilledInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';

import { useInputStyles, useFormHelperTextStyles } from './styles';
import { useDelayedCall } from '../../../hooks/useDelayCall';
import useUpdateFilterValue from '../../../hooks/useUpdateFilterValue';

const Input = ({ hiddenlabel, constraints, appliedValue, disabled, label, unit, cid, categoryId }) => {
	const { t } = useTranslation();
	const inputClasses = useInputStyles({ hiddenlabel });
	const formHelperTextClasses = useFormHelperTextStyles();
	const labelRef = useRef();
	const [labelWidth, setLabelWidth] = useState(0);
	const [localAppliedValue, setLocalAppliedValue] = useState(appliedValue);
	const min = constraints.minValue;
	const max = constraints.maxValue;
	const formHelperTextId = label + 'input-value-range';
	const isInputInvalid = min && max ? localAppliedValue < min || localAppliedValue > max : false;
	const { updateFilterValue } = useUpdateFilterValue();

	useEffect(() => {
		if (labelRef.current) setLabelWidth(labelRef.current.clientWidth);
	}, []);

	useEffect(() => {
		if (appliedValue !== localAppliedValue) setLocalAppliedValue(appliedValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appliedValue]);

	const handleBlur = () => {
		updateFilterValue(cid, categoryId, localAppliedValue);
	};

	const changeOnDelay = useDelayedCall(updateFilterValue);

	const handleChange = event => {
		const parsedValue = event.target.value === '' ? '' : Number(event.target.value);

		setLocalAppliedValue(parsedValue);
		changeOnDelay(parsedValue);
	};

	const handleSubmitValueWithEnter = event => {
		if (event.key === 'Enter') {
			document.activeElement.blur();
		}
	};

	return (
		<FormControl variant="filled" margin="dense">
			{hiddenlabel || (
				<InputLabel ref={labelRef} id={label + '_select_input_label'} htmlFor={label + '-select-input'}>
					{parse(label)}
				</InputLabel>
			)}

			<FilledInput
				disabled={disabled}
				value={localAppliedValue}
				onBlur={handleBlur}
				onChange={handleChange}
				onKeyPress={handleSubmitValueWithEnter}
				error={isInputInvalid}
				endAdornment={<InputAdornment position="end">{unit}</InputAdornment>}
				aria-describedby={formHelperTextId}
				inputProps={{
					type: 'number',
					'aria-label': label + '-input'
				}}
				labelWidth={labelWidth}
				classes={inputClasses}
			/>

			{min && max && !isInputInvalid && (
				<FormHelperText id={formHelperTextId} classes={formHelperTextClasses}>
					{t('inputFilter.range') + ': [' + min + '...' + max + ']'}
				</FormHelperText>
			)}
			{isInputInvalid && (
				<FormHelperText style={{ color: 'red' }}>
					{t('inputFilter.outOfRange') + `: [${min} ... ${max}]`}
				</FormHelperText>
			)}
		</FormControl>
	);
};

Input.propTypes = {
	hiddenlabel: PropTypes.bool,
	constraints: PropTypes.object.isRequired,
	appliedValue: PropTypes.number.isRequired,
	disabled: PropTypes.bool.isRequired,
	label: PropTypes.string.isRequired,
	unit: PropTypes.string
};

export default Input;
